import type { League } from "./types.ts";
import {
    DEFAULT_LEAGUE_BULLION_ENTRY_LOCK,
    getLeagueBullionEntryLockResolved,
    type LeagueBullionEntryLockMode,
} from "./leagueBullionEntryLockConfig.ts";

/** Why a league member cannot enter the shared Bullion battle (null = can still enter). */
export type LeagueBullionBattleEntryBlockReason = "joined_after_toss" | "entry_closed";

export type { LeagueBullionEntryLockMode };

function lockResolved() {
    return getLeagueBullionEntryLockResolved() ?? DEFAULT_LEAGUE_BULLION_ENTRY_LOCK;
}

/** @deprecated Prefer {@link getLeagueBullionEntryLockResolved}. Env/default mode until API loads. */
export const LEAGUE_BULLION_ENTRY_LOCK_MODE: LeagueBullionEntryLockMode =
    DEFAULT_LEAGUE_BULLION_ENTRY_LOCK.mode;

/** Minutes-mode window from env defaults. Prefer runtime resolved windowMs. */
export const LEAGUE_BULLION_ENTRY_LOCK_WINDOW_MS = DEFAULT_LEAGUE_BULLION_ENTRY_LOCK.minutes * 60 * 1000;

/** @deprecated Prefer runtime resolved.windowMs. */
export const LEAGUE_BULLION_ENTRY_LOCK_EFFECTIVE_WINDOW_MS = DEFAULT_LEAGUE_BULLION_ENTRY_LOCK.windowMs;

/** @deprecated Prefer runtime resolved.lockOnTossStatus. */
export const LEAGUE_BULLION_ENTRY_LOCK_ON_TOSS_STATUS = DEFAULT_LEAGUE_BULLION_ENTRY_LOCK.lockOnTossStatus;

/** @deprecated Use {@link LEAGUE_BULLION_ENTRY_LOCK_WINDOW_MS}. */
export const LEAGUE_BULLION_TOSS_ENTRY_LOCK_WINDOW_MS = LEAGUE_BULLION_ENTRY_LOCK_WINDOW_MS;

/** Human label ("5 minutes before first ball" | "at toss"). */
export function leagueBullionEntryLockWhenLabel(): string {
    return lockResolved().whenLabel;
}

/** Short label for rules / how-to copy. */
export function leagueBullionEntryLockUntilLabel(): string {
    return lockResolved().untilLabel;
}

export function leagueBullionPlaySlipLockedMessage(): string {
    return lockResolved().playSlipLockedMessage;
}

/** Wall-clock when peer Sixes / Bullion cards first unlock (kick-off minus effective lock window). */
export function leaguePeerCardRevealAtMs(
    league: Pick<League, "fixture" | "startsAt">,
): number | null {
    const startIso = league.fixture?.start ?? league.startsAt ?? null;
    const startsAtTs = startIso ? new Date(startIso).getTime() : Number.NaN;
    if (!Number.isFinite(startsAtTs)) return null;
    return startsAtTs - lockResolved().windowMs;
}

function viewerInBullionBattle(league: League, userId: number): boolean {
    if (league.bullionBattle?.viewerEntered) return true;
    if (league.bullionBattle?.enteredUserIds?.includes(userId)) return true;
    return league.plays.some((play) => play.game === "bullion" && play.userId === userId);
}

function leagueBullionStatusIndicatesInPlay(statusText: string): boolean {
    if (!statusText) return false;
    if (/\b(BAT|BATTING|BOWL|BOWLING)\b/i.test(statusText)) return true;
    if (/\bOV\.?\s*\d+(?:\.\d+)?\b/i.test(statusText)) return true;
    if (/\b\d+\s*\/\s*\d+\b/.test(statusText)) return true;
    return false;
}

function leagueBullionStatusIndicatesTossOrInPlay(statusText: string): boolean {
    if (!statusText) return false;
    if (statusText.toUpperCase().includes("TOSS")) return true;
    return leagueBullionStatusIndicatesInPlay(statusText);
}

function lockFromKickoffWindow(opts: {
    fixtureStart?: string | null;
    nowMs: number;
    windowMs: number;
}): boolean {
    const startsAtTs = opts.fixtureStart ? new Date(opts.fixtureStart).getTime() : Number.NaN;
    if (!Number.isFinite(startsAtTs)) return false;
    if (startsAtTs <= opts.nowMs) return true;
    return startsAtTs - opts.nowMs <= opts.windowMs;
}

/**
 * League Bullion play lock: configured minutes before kickoff (or toss mode), kickoff passed,
 * live, or in-play status. Toss-only status locks only when mode is `toss`.
 */
export function leagueBullionPlayEntryLocked(opts: {
    fixtureStart?: string | null;
    matchStatus?: string | null;
    isLive?: boolean;
    nowMs: number;
}): boolean {
    if (opts.isLive) return true;
    const resolved = lockResolved();
    if (
        lockFromKickoffWindow({
            fixtureStart: opts.fixtureStart,
            nowMs: opts.nowMs,
            windowMs: resolved.windowMs,
        })
    ) {
        return true;
    }
    const status = String(opts.matchStatus ?? "").trim();
    if (resolved.lockOnTossStatus) {
        return leagueBullionStatusIndicatesTossOrInPlay(status);
    }
    return leagueBullionStatusIndicatesInPlay(status);
}

/** @deprecated Use {@link leagueBullionPlayEntryLocked}. */
export const leagueBullionTossEntryLocked = leagueBullionPlayEntryLocked;

/**
 * Peer Sixes cards / Bullion slips (Activity + standings) unlock when play locks.
 */
export function leaguePeerSixesCardRevealUnlocked(
    league: Pick<League, "fixture" | "startsAt">,
    nowMs: number,
    opts: { matchStatus?: string | null; isLive?: boolean } = {},
): boolean {
    return leagueBullionPlayEntryLocked({
        fixtureStart: league.fixture?.start ?? league.startsAt ?? null,
        matchStatus: opts.matchStatus,
        isLive: Boolean(opts.isLive),
        nowMs,
    });
}

/** Alias — Bullion peer card summaries use the same unlock window as Sixes. */
export const leaguePeerBullionCardRevealUnlocked = leaguePeerSixesCardRevealUnlocked;

/** Peer reveal for Sixes or Bullion (Activity / Open Plays / member standings). */
export function leaguePeerCardRevealUnlocked(
    league: Pick<League, "fixture" | "startsAt">,
    nowMs: number,
    opts: { matchStatus?: string | null; isLive?: boolean } = {},
): boolean {
    return leaguePeerSixesCardRevealUnlocked(league, nowMs, opts);
}

/**
 * Whether the viewer is blocked from entering the league Bullion battle.
 * Distinguishes late joiners (joined after the lock started) from members who simply missed entry.
 */
export function leagueBullionBattleEntryBlock(
    league: League,
    viewerUserId: number | null | undefined,
    opts: {
        fixtureStart?: string | null;
        matchStatus?: string | null;
        isLive?: boolean;
        nowMs?: number;
    } = {},
): LeagueBullionBattleEntryBlockReason | null {
    if (viewerUserId == null) return null;
    if (viewerInBullionBattle(league, viewerUserId)) return null;
    const me = league.members.find((member) => member.userId === viewerUserId);
    if (!me) return null;

    const nowMs = opts.nowMs ?? Date.now();
    const startsAt = opts.fixtureStart ?? league.fixture?.start ?? league.startsAt ?? null;
    const locked = leagueBullionPlayEntryLocked({
        fixtureStart: startsAt,
        matchStatus: opts.matchStatus,
        isLive: Boolean(opts.isLive),
        nowMs,
    });
    if (!locked) return null;

    const startsAtTs = startsAt ? new Date(startsAt).getTime() : Number.NaN;
    const lockStartMs = Number.isFinite(startsAtTs)
        ? startsAtTs - lockResolved().windowMs
        : Number.NaN;
    const joinedAtMs = new Date(me.joinedAt).getTime();
    if (Number.isFinite(lockStartMs) && Number.isFinite(joinedAtMs) && joinedAtMs >= lockStartMs) {
        return "joined_after_toss";
    }
    return "entry_closed";
}
